import React, { FC } from "react";
import {
  StyleSheet,
  Text,
  View,
  TouchableOpacity,
  Platform,
} from "react-native";
import Constants from "expo-constants";
import { AvatarText } from "../../components/common/Avatar";
import GenericHeader from "../../components/headers/GenericHeader";
import StyledIcon from "../../components/StyledIcon";
import Colors from "../../constants/Colors";
import FontStyles from "../../constants/FontStyle";
import { useNavigation } from "@react-navigation/native";
import { AuthContext } from "../../contexts/auth/Store";
import { IBuyer } from "../../interfaces/buyer";
import { ILedger, LedgerHead, LedgerSubhead } from "../../interfaces/ledger";
import useFetch from "../../hooks/useFetch";
import { ledgerService } from "../../services/LedgerService";
import { formatCurrency } from "../../utils/money";

const MenuButton: FC<{ action?: () => void }> = ({ action, ...props }) => {
  return (
    <TouchableOpacity
      style={styles.actionButtonContainer}
      onPress={() => {
        action && action();
      }}
    >
      {props.children}
    </TouchableOpacity>
  );
};

const LeftIconProps = {
  iconColor: Colors.tintColor,
  fillColor: "#E9FAFF",
};
const RightIconProps = {
  iconColor: Colors.greyColor,
  iconOnly: true,
  size: 14,
};

export const SettingsScreen: FC = () => {
  const navigation = useNavigation();
  const auth: any = React.useContext(AuthContext);
  const buyer: IBuyer = auth?.state?.buyerData;

  const [ledger, loadingLedger] = useFetch<ILedger>(
    ledgerService,
    "fetchByOwnerSpecific",
    [
      {
        head: LedgerHead.Buyer,
        subhead: LedgerSubhead.Wallet,
        owner: buyer?._id,
      },
    ],
    !!buyer?._id
  );

  const updateApp = () => {
    if (Platform.OS === "web") {
      if ("serviceWorker" in navigator) {
        navigator.serviceWorker.getRegistration().then(async function (reg) {
          if (reg) {
            // await reg.update()
            reg.unregister().then(function () {
              window.location.reload();
            });
          } else {
            window.location.reload();
          }
        });
      } else {
        console.log("Service worker not supported.");
        window.location.reload();
      }
    }
  };

  const doSignout = () => {
    auth.signOut();
    setTimeout(() => navigation.navigate("Intro"), 500);
  };

  return (
    <View style={{ flex: 1, backgroundColor: Colors.whiteColor }}>
      <GenericHeader title={`Settings`} hideBackButton={true} />
      <View style={styles.card}>
        <AvatarText title={buyer?.name} />
        <Text style={styles.cardHeader}>{buyer?.name}</Text>
        <Text style={styles.cardSubHeader}>{buyer?.phone}</Text>
      </View>

      <View style={styles.container}>
        {/* version */}
        <MenuButton action={updateApp}>
          <StyledIcon iconName="download" {...LeftIconProps} />
          <Text style={styles.actionButtonLabel}>Update App</Text>
          <Text
            style={{
              ...FontStyles.size14,
              ...FontStyles.lh16,
              ...FontStyles.medium,
              ...FontStyles.gray,
            }}
          >
            v{Constants.manifest.version}
          </Text>
        </MenuButton>

        {/* balance */}
        {/* <MenuButton action={() => navigation.navigate("Balance")}>
          <StyledIcon
            iconName="credit-card"
            iconLibrary="Feather"
            {...LeftIconProps}
          />
          <Text style={styles.actionButtonLabel}>Balance</Text>
          <Text
            style={[
              {
                ...FontStyles.size14,
                ...FontStyles.lh16,
                ...FontStyles.medium,
                ...FontStyles.gray,
              },
              ledger
                ? {
                    color:
                      ledger?.balance < 0
                        ? Colors.dangerText
                        : Colors.successText,
                  }
                : undefined,
            ]}
          >
            {loadingLedger
              ? "..."
              : ledger
              ? formatCurrency(ledger?.balance || 0, false, true)
              : "-"}
          </Text>
        </MenuButton> */}

        {/* edit profile */}
        <MenuButton action={() => navigation.navigate("Profile")}>
          <StyledIcon iconName="person" {...LeftIconProps} />
          <Text style={styles.actionButtonLabel}>Edit Profile</Text>
          <StyledIcon
            iconName="chevron-right"
            iconLibrary="Feather"
            {...RightIconProps}
          />
        </MenuButton>

        {/* support */}
        {/* <MenuButton>
          <StyledIcon iconName="help" {...LeftIconProps} />
          <Text style={styles.actionButtonLabel}>Support</Text>
          <StyledIcon
            iconName="chevron-right"
            iconLibrary="Feather"
            {...RightIconProps}
          />
        </MenuButton> */}

        {/* voucher */}
        <MenuButton action={() => navigation.navigate("Voucher")}>
          <StyledIcon
            iconName="ticket-confirmation"
            iconLibrary="MaterialCommunityIcons"
            {...LeftIconProps}
          />
          <Text style={styles.actionButtonLabel}>Voucher</Text>
          <StyledIcon
            iconName="chevron-right"
            iconLibrary="Feather"
            {...RightIconProps}
          />
        </MenuButton>

        {/* about */}
        <MenuButton action={() => navigation.navigate("About")}>
          <StyledIcon iconName="help" {...LeftIconProps} />
          <Text style={styles.actionButtonLabel}>About</Text>
          <StyledIcon
            iconName="chevron-right"
            iconLibrary="Feather"
            {...RightIconProps}
          />
        </MenuButton>

        {/* sign out */}
        <MenuButton action={doSignout}>
          <StyledIcon
            iconName="log-out"
            iconColor={Colors.dangerText}
            fillColor={Colors.dangerBackground}
          />
          <Text
            style={[styles.actionButtonLabel, { color: Colors.dangerText }]}
          >
            Sign out
          </Text>
        </MenuButton>

        <Text style={[styles.appVersionText]}>
          App Version {Constants.manifest.version} (
          {Constants.manifest.extra.versionCode})
        </Text>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  card: {
    alignItems: "center",
    padding: 20,
  },
  cardHeader: {
    ...FontStyles.semibold,
    ...FontStyles.size16,
    ...FontStyles.lh20,
    ...FontStyles.dark,
    marginTop: 10,
  },
  cardSubHeader: {
    ...FontStyles.normal,
    ...FontStyles.size14,
    ...FontStyles.lh16,
    ...FontStyles.gray,
    marginTop: 4,
  },
  container: {
    paddingHorizontal: 16,
  },
  actionButtonContainer: {
    marginBottom: 10,
    borderColor: Colors.borderColor,
    borderWidth: 1,
    borderStyle: "solid",
    borderRadius: 8,
    paddingVertical: 12,
    paddingHorizontal: 16,
    flexDirection: "row",
    alignContent: "center",
    alignItems: "center",
  },
  actionButtonLabel: {
    flexGrow: 1,
    ...FontStyles.size14,
    ...FontStyles.lh16,
    ...FontStyles.medium,
    paddingLeft: 8,
  },
  appVersionText: {
    ...FontStyles.size13,
    ...FontStyles.lh16,
    ...FontStyles.normal,
    ...FontStyles.gray,
    marginTop: 26,
  },
});
